import { useEffect } from 'react';

import { useMagicIslandController } from './MagicIslandContext';
import type { IslandMode } from './types';

/**
 * Forces the Magic Island to `mode` regardless of scroll position, releasing it
 * back to the scroll-driven sections when `mode` is null or on unmount. For
 * always-on surfaces such as Bulk Enquire that must show while a selection is
 * active.
 *
 * - A no-op when there is no provider (feature off), so callers can call it
 *   unconditionally.
 * - `mode` must be referentially stable across renders (wrap it in `useMemo`),
 *   otherwise the effect re-runs every render.
 * - Single-slot: the controller holds one override, so only one always-on
 *   surface should use this per island.
 */
export const useIslandOverride = (mode: IslandMode | null) => {
    const controller = useMagicIslandController();

    useEffect(() => {
        if (!controller) return;
        controller.setOverride(mode);
        return () => controller.setOverride(null);
    }, [controller, mode]);
};
